import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useRef, useState } from 'react';
import { BrowseNavigationItemSelected, BrowseNavigationItemStyle, BrowseNavigationItemType, UFBNoticeImpressionEvent } from '@udemy/browse-event-tracking';
import { ClickEvent, TrackImpression, Tracker } from '@udemy/event-tracking';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { LanguageSelectorButton, LanguageSeoLinks } from '@udemy/language-selector';
import { ProBadge } from '@udemy/react-pro-content';
import { UDEMY_PRO_PLACEHOLDER_URL } from '@udemy/react-subscription-experience';
import { SubscriptionFeatureKey, useSubscriptionFeatures, withSubscriptionFeaturesProvider } from '@udemy/react-subscription-features-provider';
import { range } from '@udemy/shared-utils';
import { udLink, useUDData } from '@udemy/ud-data';
import { ProfileBadge } from '../../badges.react-component';
import { AGGREGATOR_NAV, ACADEMIES_NAV, AI_LEARNING_NAV, BADGING_NAV, BROWSE_LEARNING_TYPES, ROLE_PLAY_NAV } from '../../browse-constants';
import { LanguageSelectorLocation } from '../../constants';
import { BadgeNavItemSelectEvent, CategoryNavItemSelectEvent, GWG_CERTIFICATION_TYPE, LEARN_AI_WITH_GOOGLE_NAV_ID, LearningTypeNavItemSelectEvent, LoginSelected, SignupSelected } from '../../events';
import { showUserActivityLink } from '../../external/header-experiment-features';
import { MOST_POPULAR_SUBCATEGORY_IDS } from '../../fetch-popular-topics';
import { useHeaderExperimentFeatures } from '../../hooks/use-header-experiment-features';
import { useHeaderStore } from '../../hooks/use-header-store';
import { useMobileNavStore } from '../../hooks/use-mobile-nav-store';
import { usePlansAndPricing } from '../../hooks/use-plans-and-pricing';
import { useUfbContext } from '../../hooks/use-ufb-context';
import { LanguageSelectorOverlay } from './language-selector/language-selector-overlay.react-component';
import { MobileAcademiesNav } from './level-one/mobile-academies-nav.react-component';
import { MobileAggregatorNav } from './level-one/mobile-aggregator-nav.react-component';
import { MobileAIAssistantNav } from './level-one/mobile-ai-assistant-nav.react-component';
import { MobileCategoryNav } from './level-one/mobile-category-nav.react-component';
import { MobileFindCoursesNav } from './level-one/mobile-find-courses-nav.react-component';
import { MobileGoogleCertificateNav } from './level-one/mobile-google-certificate-nav.react-component';
import { MobileLearnAINav } from './level-one/mobile-learn-ai-nav.react-component';
import { MobilePlansAndPricingNav } from './level-one/mobile-plans-and-pricing-nav.react-component';
import { MobilePopularTopicNavs } from './level-one/mobile-popular-topic-navs.react-component';
import { MobileStudentProfileNav } from './level-one/mobile-student-profile-nav.react-component';
import { MobileUdemyBusinessNav } from './level-one/mobile-udemy-business-nav.react-component';
import { MobileAcademiesNavL2 } from './level-two/mobile-academies-nav.react-component';
import { MobileSubcategoryNav } from './level-two/mobile-subcategory-nav.react-component';
import styles from "./mobile-nav.module.css";
import { MobileNav, MobileNavItem, MobileNavSection, MobileNavSectionHeading, MobileNavWelcomeSection } from './mobile-nav.react-component';
import { MobileSubscribeButton } from './subscribe/mobile-subscribe-button.react-component';
const MobileIAStudentNavInternal = observer(({
  useLangPrefixedUrls
}) => {
  var _headerStore$userSpec, _ufbContext$subNavs, _ufbContext$manageAnd, _ufbContext$browseSec;
  const {
    gettext,
    locale
  } = useI18n();
  const headerStore = useHeaderStore();
  const mobileNavStore = useMobileNavStore();
  const ufbContext = useUfbContext();
  const udData = useUDData();
  const headerExperimentFeatures = useHeaderExperimentFeatures();
  const {
    showPlansAndPricingDropdown
  } = usePlansAndPricing();
  const showActivityLink = showUserActivityLink({
    headerExperimentFeatures,
    headerStore
  });
  // aggregator experiment flags
  const isUBUser = udData.me.is_authenticated && !!udData.me.organization;
  const isPPUser = headerStore.isPersonalPlanSubscriber;
  const isTPUser = isUBUser && udData.Config.brand.is_team;
  const isAcademiesFeatureEnabled = headerStore.isAcademiesFeatureEnabled;
  const isAggregatorFeatureEnabled = headerStore.isAggregatorFeatureEnabled;
  const isLearnAINavigationFeatureEnabled = headerStore.isLearnAINavigationFeatureEnabled;
  const isRolePlayFeatureEnabled = headerStore.isRolePlayFeatureEnabled;
  const isMX = !isUBUser && !isPPUser;
  const showSplitCategoriesNav = (headerExperimentFeatures === null || headerExperimentFeatures === void 0 ? void 0 : headerExperimentFeatures.isSplitNavByCategoriesEnabled) && isMX;
  const isCoachingFeatureEnabled = udData.Config.features.coaching;
  const isGrowWithGoogleEnabled = headerStore.isGrowWithGoogleNavigationEnabled;
  const subscriptionFeaturesResult = useSubscriptionFeatures([SubscriptionFeatureKey.Assessments]);
  const hasAssessmentsSubscriptionFeature = Boolean(subscriptionFeaturesResult.subscriptionFeatures.get(SubscriptionFeatureKey.Assessments));
  const shouldRenderAssessmentsInGoalsSection = ((_headerStore$userSpec = headerStore.userSpecificContext.user) === null || _headerStore$userSpec === void 0 ? void 0 : _headerStore$userSpec.enableAssessmentsInPersonalPlan) || hasAssessmentsSubscriptionFeature;
  const udMe = udData.me;
  const sideDrawerRef = useRef(null);
  const [isLanguageSelectorOpen, setLanguageSelectorOpen] = useState(false);
  function handleLanguageSelectorButtonClick() {
    var _sideDrawerRef$curren;
    setLanguageSelectorOpen(true);
    (_sideDrawerRef$curren = sideDrawerRef.current) === null || _sideDrawerRef$curren === void 0 || _sideDrawerRef$curren.scrollTo(0, 0);
  }
  function handleBrowseLearningItemClick(item) {
    Tracker.publishEvent(new LearningTypeNavItemSelectEvent(item.type));
  }
  function handleBadgeNavItemClick(item) {
    Tracker.publishEvent(new BadgeNavItemSelectEvent(item.type));
  }
  function handleGenericNavItemClick(item) {
    const context = {
      type: item.type,
      id: String(item.id),
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      uiRegion: 'main_nav'
    };
    Tracker.publishEvent(new BrowseNavigationItemSelected(context));
  }
  function handleAcademiesNavItemClick(item) {
    var _item$slug;
    const context = {
      type: item.type,
      id: (_item$slug = item.slug) !== null && _item$slug !== void 0 ? _item$slug : String(item.id),
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      uiRegion: 'main_nav'
    };
    Tracker.publishEvent(new BrowseNavigationItemSelected(context));
  }
  function handleSubcategoryNavClick(item) {
    const context = {
      categoryId: item.parentId,
      subcategoryId: item.id,
      topicId: null,
      uiRegion: 'mobile_nav'
    };
    Tracker.publishEvent(new CategoryNavItemSelectEvent({
      context
    }));
  }
  function onToggle() {
    setLanguageSelectorOpen(false);
    if (headerStore.userSpecificContext.isLoggedIn) {
      mobileNavStore.ensureLevelIsLoaded(1);
      mobileNavStore.loadMostPopularTopicsForSubcategories(locale);
    }
  }
  function loggedOutSection() {
    const {
      user
    } = headerStore.userSpecificContext;
    if (!user || user.id) {
      return null;
    }
    return /*#__PURE__*/React.createElement(MobileNavSection, null, /*#__PURE__*/React.createElement(MobileNavItem, {
      href: udLink.toAuth({
        showLogin: true,
        responseType: 'html'
      }),
      color: "link",
      onClick: () => Tracker.publishEvent(new LoginSelected())
    }, gettext('Log in')), /*#__PURE__*/React.createElement(MobileNavItem, {
      href: udLink.toAuth({
        ...headerStore.signupParams,
        responseType: 'html'
      }),
      color: "link",
      "data-purpose": "header-signup-mobile-nav",
      onClick: () => Tracker.publishEvent(new SignupSelected())
    }, gettext('Sign up')), /*#__PURE__*/React.createElement(MobileSubscribeButton, {
      color: "link",
      "data-purpose": "header-subscribe-mobile-nav"
    }));
  }
  function iaSwitchSection() {
    const {
      isInstructor,
      user
    } = headerStore.userSpecificContext;
    if (!user || !user.id || !isInstructor) {
      return null;
    }
    return /*#__PURE__*/React.createElement(MobileNavSection, null, /*#__PURE__*/React.createElement(MobileNavItem, {
      href: headerStore.urls.TEACH,
      color: "link"
    }, gettext('Switch to instructor view')));
  }
  function learnSection() {
    const {
      user
    } = headerStore.userSpecificContext;
    if (!user || !user.id) {
      return null;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavSectionHeading, null, gettext('Learn')), /*#__PURE__*/React.createElement(MobileNavSection, null, /*#__PURE__*/React.createElement(MobileNavItem, {
      href: headerStore.urls.MY_LEARNING
    }, gettext('My learning')), isCoachingFeatureEnabled && /*#__PURE__*/React.createElement(MobileNavItem, {
      href: headerStore.urls.COACHING_BOOKINGS
    }, gettext('My 1-1 bookings')), showActivityLink && /*#__PURE__*/React.createElement(MobileNavItem, {
      href: headerStore.urls.MY_ACTIVITY,
      onClick: () => {
        Tracker.publishEvent(new ClickEvent({
          componentName: 'headerToMyActivity'
        }));
      }
    }, gettext('My activity')), user.sms_subscriptions_active && /*#__PURE__*/React.createElement(MobileNavItem, {
      href: UDEMY_PRO_PLACEHOLDER_URL,
      color: "link"
    }, gettext('All programs')), headerStore.enableCartOnMobileNav && headerStore.notificationBadgeContext.cartBuyables > 0 && /*#__PURE__*/React.createElement(MobileNavItem, {
      href: headerStore.urls.CART
    }, gettext('My cart'))));
  }
  function showPersonalPlanNav() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.consumer_subscription_active;
  }
  function showUBProNav() {
    var _organization;
    const udConfig = udData.Config;
    return udConfig.brand.has_organization && (udConfig.features.organization.learning_path.pro_path || (udMe === null || udMe === void 0 || (_organization = udMe.organization) === null || _organization === void 0 ? void 0 : _organization.is_pro_license_holder));
  }
  function showBadgingNavChangesForPPUB() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.show_updated_pp_and_ub_navigation;
  }
  function showBadgingNavChangesForMX() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.show_updated_mx_navigation;
  }
  function showLearnerOrganizationBadges() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.show_learner_organization_badges;
  }
  function showCategoriesNavForUB() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.show_category_navigation_for_ub_users;
  }
  function showCertPrepEntryPointForUB() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.show_certificate_content_for_ub_users;
  }
  function showAIElevateEntryPointForUB() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.show_ai_elevate_for_ub_users;
  }
  function showAIAssistant() {
    const {
      user
    } = headerStore.userSpecificContext;
    return user === null || user === void 0 ? void 0 : user.show_ai_assistant;
  }
  function showAggregatorMenus() {
    // aggregator menus shown for MX users who are not UB or PP users
    return isAggregatorFeatureEnabled && !isUBUser && !isPPUser;
  }
  function showLearnAINav() {
    return isLearnAINavigationFeatureEnabled;
  }
  function showAcademiesMenus() {
    var _headerStore$userSpec2;
    // academies menus shown for MX users who are not UB or PP users
    return isAcademiesFeatureEnabled && !isUBUser && !!((_headerStore$userSpec2 = headerStore.userSpecificContext.careerAcceleratorsNav) !== null && _headerStore$userSpec2 !== void 0 && _headerStore$userSpec2.length) || showSplitCategoriesNav;
  }
  function showGoogleCertificateMenus() {
    // isGrowWithGoogleEnabled already checks for subscription plan AND nav data
    // We only need to exclude UB users here
    return isGrowWithGoogleEnabled && !isUBUser;
  }
  function showRolePlayNav() {
    return isRolePlayFeatureEnabled;
  }
  function showCertPrepNav() {
    if (isUBUser || isPPUser || isTPUser) {
      return showCertPrepEntryPointForUB();
    }
    return true;
  }
  function renderLearningTypeNavItem(item) {
    return /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": `link-${item.type}`,
      onClick: () => handleBrowseLearningItemClick(item),
      href: item.absolute_url
    }, item.title);
  }
  function learningTypesSectionTitle() {
    if (showPersonalPlanNav() && showBadgingNavChangesForPPUB()) return gettext('Explore active learning ');else if (showUBProNav() && showBadgingNavChangesForPPUB()) return gettext('Explore Pro');
    return gettext('Explore by type');
  }
  function learningTypesSection() {
    const {
      user
    } = headerStore.userSpecificContext;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavSectionHeading, {
      "data-purpose": "section-active-learning"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['learning-types-section']
    }, learningTypesSectionTitle(), showUBProNav() && /*#__PURE__*/React.createElement(ProBadge, null))), /*#__PURE__*/React.createElement(MobileNavSection, null, ((user === null || user === void 0 ? void 0 : user.enableLabsInPersonalPlan) || showUBProNav()) && renderLearningTypeNavItem(BROWSE_LEARNING_TYPES(gettext).LABS), showUBProNav() && showBadgingNavChangesForPPUB() && renderLearningTypeNavItem(BROWSE_LEARNING_TYPES(gettext).UDEMY_PRO_PATHS)));
  }

  /**
   * Handles click events for the L1 'Learn AI with Google' nav item in the main menu.
   * Fires a BrowseNavigationItemSelected event with learn-ai-with-google type.
   */
  function handleLearnAIWithGoogleL1Click() {
    const context = {
      id: LEARN_AI_WITH_GOOGLE_NAV_ID,
      type: GWG_CERTIFICATION_TYPE,
      uiRegion: 'main_nav',
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN
    };
    Tracker.publishEvent(new BrowseNavigationItemSelected(context));
  }

  /**
   * Handles click events for the L1 'Find Courses' nav item in the split categories nav.
   * Fires a BrowseNavigationItemSelected event with category type.
   */
  function handleFindCoursesL1Click() {
    const context = {
      type: BrowseNavigationItemType.CATEGORY,
      id: 'find-courses',
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      uiRegion: 'main_nav'
    };
    Tracker.publishEvent(new BrowseNavigationItemSelected(context));
  }
  function newAndFeaturedSection() {
    if (!showGoogleCertificateMenus()) {
      return null;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavSectionHeading, {
      "data-purpose": "section-new-featured"
    }, gettext('New & Featured')), googleCertificateSection());
  }
  function googleCertificateSection() {
    return /*#__PURE__*/React.createElement(MobileNavSection, null, /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": "google-certificate-link",
      cssToggleId: "header-toggle-side-nav-google-certificates",
      onClick: handleLearnAIWithGoogleL1Click
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['google-certificate-heading'], {
        [styles['category-title']]: showSplitCategoriesNav
      })
    }, /*#__PURE__*/React.createElement(Icon, {
      size: "xsmall",
      glyph: "google",
      label: ""
    }), gettext('Learn AI with Google'))));
  }
  function goalsSection() {
    var _brand$organization;
    // Hide Goals section for UB China
    const {
      brand
    } = udData.Config;
    if (brand.organization && (_brand$organization = brand.organization) !== null && _brand$organization !== void 0 && _brand$organization.is_enterprise_china) {
      return null;
    }
    if (showSplitCategoriesNav) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavSection, null, /*#__PURE__*/React.createElement(MobileNavItem, {
        "data-purpose": "find-courses-link",
        cssToggleId: "header-toggle-side-nav-find-courses",
        onClick: handleFindCoursesL1Click
      }, /*#__PURE__*/React.createElement("span", {
        className: styles['category-title']
      }, gettext('Find Courses'))), /*#__PURE__*/React.createElement(MobileNavItem, {
        "data-purpose": "learn-ai-link",
        cssToggleId: "header-toggle-side-nav-learn-ai",
        onClick: () => handleGenericNavItemClick(AI_LEARNING_NAV(gettext).AI_LEARNING)
      }, /*#__PURE__*/React.createElement("span", {
        className: styles['category-title']
      }, gettext('Learn AI'))), /*#__PURE__*/React.createElement(MobileNavItem, {
        "data-purpose": "certification-aggregators-link",
        cssToggleId: "header-toggle-side-nav-get-certified",
        onClick: () => handleGenericNavItemClick(AGGREGATOR_NAV(gettext).AGGREGATORS)
      }, /*#__PURE__*/React.createElement("span", {
        className: styles['category-title']
      }, gettext('Get Certified'))), /*#__PURE__*/React.createElement(MobileNavItem, {
        "data-purpose": "role-play-link",
        href: ROLE_PLAY_NAV(gettext).ROLE_PLAY.absolute_url,
        onClick: () => handleGenericNavItemClick(ROLE_PLAY_NAV(gettext).ROLE_PLAY)
      }, /*#__PURE__*/React.createElement("span", {
        className: styles['category-title']
      }, gettext('Courses with Role Play')))));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavSectionHeading, {
      "data-purpose": "section-goals"
    }, gettext('Explore by Goal')), /*#__PURE__*/React.createElement(MobileNavSection, null, showLearnAINav() && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": "learn-ai-link",
      cssToggleId: "header-toggle-side-nav-learn-ai"
    }, gettext('Learn AI'))), showAcademiesMenus() && /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": "academies-link",
      cssToggleId: "header-toggle-side-nav-academies",
      onClick: () => handleAcademiesNavItemClick(ACADEMIES_NAV(gettext).ALL_ACADEMIES)
    }, ACADEMIES_NAV(gettext).ALL_ACADEMIES.title), showAggregatorMenus() && showCertPrepNav() && /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": "certification-aggregators-link",
      cssToggleId: "header-toggle-side-nav-certifications",
      onClick: () => handleGenericNavItemClick(AGGREGATOR_NAV(gettext).AGGREGATORS)
    }, AGGREGATOR_NAV(gettext, true).AGGREGATORS.title), !showAggregatorMenus() && (showBadgingNavChangesForMX() || showBadgingNavChangesForPPUB()) && /*#__PURE__*/React.createElement(React.Fragment, null, showCertPrepNav() && /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": "certifications-link",
      onClick: () => handleBadgeNavItemClick(BADGING_NAV(gettext).CERTIFICATIONS),
      href: BADGING_NAV(gettext).CERTIFICATIONS.absolute_url
    }, BADGING_NAV(gettext).CERTIFICATIONS.title), showLearnerOrganizationBadges() && /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": "organization-badges-link",
      onClick: () => handleBadgeNavItemClick(BADGING_NAV(gettext).ORGANIZATION_BADGES),
      href: BADGING_NAV(gettext).ORGANIZATION_BADGES.absolute_url
    }, BADGING_NAV(gettext).ORGANIZATION_BADGES.title), shouldRenderAssessmentsInGoalsSection && renderLearningTypeNavItem(BADGING_NAV(gettext).ASSESSMENTS_NAV)), showRolePlayNav() && /*#__PURE__*/React.createElement(MobileNavItem, {
      "data-purpose": "role-play-link",
      href: ROLE_PLAY_NAV(gettext).ROLE_PLAY.absolute_url,
      onClick: () => handleGenericNavItemClick(ROLE_PLAY_NAV(gettext).ROLE_PLAY)
    }, ROLE_PLAY_NAV(gettext).ROLE_PLAY.title)));
  }
  function mostPopularSection() {
    if ((isUBUser || isPPUser || isTPUser) && !showCategoriesNavForUB()) {
      return null;
    } else if (showSplitCategoriesNav) {
      return null;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavSectionHeading, null, showPersonalPlanNav() || showBadgingNavChangesForMX() ? gettext('Explore by category') : gettext('Most popular')), /*#__PURE__*/React.createElement(MobileNavSection, null, mobileNavStore.mostPopularSubcategories ? mobileNavStore.mostPopularSubcategories.map(s => /*#__PURE__*/React.createElement(MobileNavItem, {
      onClick: () => handleSubcategoryNavClick(s),
      key: `most-popular-subcategory-item-${s.id}`,
      cssToggleId: `header-toggle-side-nav-popular-topics-of-${s.id}`,
      "data-purpose": "most-popular-subcategory-item"
    }, s.title)) : range(MOST_POPULAR_SUBCATEGORY_IDS.length).map(i => /*#__PURE__*/React.createElement(MobileNavItem, {
      key: `most-popular-id-${i}`,
      loading: true
    })), mobileNavStore.navigationCategories ? /*#__PURE__*/React.createElement(MobileNavItem, {
      cssToggleId: "header-toggle-side-nav-categories"
    }, gettext('All categories')) : /*#__PURE__*/React.createElement(MobileNavItem, {
      loading: true
    })));
  }
  const ubHookPlacement = 'ufb_header_mobile';
  function trackUFBNoticeImpressionEvent() {
    Tracker.publishEvent(new UFBNoticeImpressionEvent({
      locale,
      placement: ubHookPlacement
    }));
  }
  function moreFromUdemySection() {
    const udConfig = udData.Config;
    const {
      mobileAppLink,
      urls
    } = headerStore;
    const hasOrganization = udConfig.brand.has_organization;
    const {
      user
    } = headerStore.userSpecificContext;
    const isLoggedIn = user && user.id;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileNavSectionHeading, null, gettext('More from Udemy')), /*#__PURE__*/React.createElement(MobileNavSection, null, isLoggedIn && /*#__PURE__*/React.createElement(MobileSubscribeButton, {
      color: "link",
      "data-purpose": "header-subscribe-mobile-nav-logged-in"
    }), !hasOrganization && /*#__PURE__*/React.createElement(TrackImpression, {
      trackFunc: trackUFBNoticeImpressionEvent
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(MobileNavItem, {
      cssToggleId: "header-toggle-side-nav-udemy-business-nav"
    }, gettext('Udemy Business')))), /*#__PURE__*/React.createElement(MobileNavItem, {
      href: mobileAppLink === null || mobileAppLink === void 0 ? void 0 : mobileAppLink.url,
      target: "_blank",
      rel: "noopener noreferrer"
    }, gettext('Get the app')), !hasOrganization && /*#__PURE__*/React.createElement(MobileNavItem, {
      href: urls.INVITE
    }, gettext('Invite friends')), /*#__PURE__*/React.createElement(MobileNavItem, {
      href: urls.SUPPORT,
      target: "_blank",
      rel: "noopener noreferrer"
    }, gettext('Help and Support'))));
  }
  function subNavs() {
    var _mobileNavStore$navig;
    const udConfig = udData.Config;
    const hasOrganization = udConfig.brand.has_organization;
    /** When true, drawers are rendered in main menu order so SubDrawer toggles resolve correctly. */
    const split = showSplitCategoriesNav;
    return /*#__PURE__*/React.createElement(React.Fragment, null, split && /*#__PURE__*/React.createElement(React.Fragment, null, showGoogleCertificateMenus() && /*#__PURE__*/React.createElement(MobileGoogleCertificateNav, null), mobileNavStore.isLevelLoaded(1) && /*#__PURE__*/React.createElement(MobileFindCoursesNav, null), mobileNavStore.isLevelLoaded(2) && showAcademiesMenus() && /*#__PURE__*/React.createElement(MobileAcademiesNavL2, null), showLearnAINav() && /*#__PURE__*/React.createElement(MobileLearnAINav, {
      isSplitNavByCategoriesEnabled: true
    }), showAggregatorMenus() && /*#__PURE__*/React.createElement(MobileAggregatorNav, {
      isSplitNavByCategoriesEnabled: true
    })), !split && /*#__PURE__*/React.createElement(React.Fragment, null, showLearnAINav() && /*#__PURE__*/React.createElement(MobileLearnAINav, {
      isSplitNavByCategoriesEnabled: false
    }), showAggregatorMenus() && /*#__PURE__*/React.createElement(MobileAggregatorNav, {
      isSplitNavByCategoriesEnabled: false
    }), mobileNavStore.isLevelLoaded(1) && showAcademiesMenus() && /*#__PURE__*/React.createElement(MobileAcademiesNav, null), showPlansAndPricingDropdown && /*#__PURE__*/React.createElement(MobilePlansAndPricingNav, null)), /*#__PURE__*/React.createElement("div", null, mobileNavStore.isLevelLoaded(2) && ((_mobileNavStore$navig = mobileNavStore.navigationCategories) === null || _mobileNavStore$navig === void 0 ? void 0 : _mobileNavStore$navig.map(category => /*#__PURE__*/React.createElement(MobileSubcategoryNav, {
      key: `mobile-subcategory-nav-${category.id}`,
      parentCategory: category,
      subcategories: category.children
    }))), mobileNavStore.isLevelLoaded(1) && /*#__PURE__*/React.createElement(MobileCategoryNav, null)), mobileNavStore.isLevelLoaded(1) && /*#__PURE__*/React.createElement(MobilePopularTopicNavs, null), mobileNavStore.isLevelLoaded(1) && /*#__PURE__*/React.createElement(MobileStudentProfileNav, null), !split && showGoogleCertificateMenus() && /*#__PURE__*/React.createElement(MobileGoogleCertificateNav, null), !hasOrganization && /*#__PURE__*/React.createElement(MobileUdemyBusinessNav, null));
  }
  const {
    user
  } = headerStore.userSpecificContext;
  return /*#__PURE__*/React.createElement(MobileNav, {
    ref: sideDrawerRef,
    onToggle: onToggle,
    subDrawers: (_ufbContext$subNavs = ufbContext === null || ufbContext === void 0 ? void 0 : ufbContext.subNavs) !== null && _ufbContext$subNavs !== void 0 ? _ufbContext$subNavs : subNavs()
  }, isLanguageSelectorOpen ? /*#__PURE__*/React.createElement(LanguageSelectorOverlay, {
    useLangPrefixedUrls: useLangPrefixedUrls
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, loggedOutSection(), /*#__PURE__*/React.createElement(MobileNavWelcomeSection, {
    cssToggleId: "header-toggle-side-nav-student-profile",
    badge: /*#__PURE__*/React.createElement(ProfileBadge, {
      dot: true,
      className: styles['profile-badge']
    })
  }), (_ufbContext$manageAnd = ufbContext === null || ufbContext === void 0 ? void 0 : ufbContext.manageAndTeachSection) !== null && _ufbContext$manageAnd !== void 0 ? _ufbContext$manageAnd : iaSwitchSection(), (showAIAssistant() || showAIElevateEntryPointForUB()) && /*#__PURE__*/React.createElement(MobileAIAssistantNav, {
    showAIElevateEntryPoint: showAIElevateEntryPointForUB(),
    shouldEnableAIAssistant: showAIAssistant()
  }), learnSection(), showSplitCategoriesNav ? googleCertificateSection() : newAndFeaturedSection(), (showSplitCategoriesNav || showBadgingNavChangesForPPUB() || showLearnAINav() || showBadgingNavChangesForMX() || showAggregatorMenus() || showAcademiesMenus() || showRolePlayNav()) && goalsSection(), (showPersonalPlanNav() || showUBProNav() && showBadgingNavChangesForPPUB()) && learningTypesSection(), !showSplitCategoriesNav && ((_ufbContext$browseSec = ufbContext === null || ufbContext === void 0 ? void 0 : ufbContext.browseSection) !== null && _ufbContext$browseSec !== void 0 ? _ufbContext$browseSec : mostPopularSection()), moreFromUdemySection(), /*#__PURE__*/React.createElement("div", {
    className: styles['nav-item'],
    "data-testid": "language-selector"
  }, /*#__PURE__*/React.createElement(LanguageSelectorButton, {
    publishEvents: {
      uiRegion: LanguageSelectorLocation.MOBILE_NAV
    },
    onClick: handleLanguageSelectorButtonClick
  }), user && /*#__PURE__*/React.createElement(LanguageSeoLinks, null))));
});
export const MobileIAStudentNav = withSubscriptionFeaturesProvider([SubscriptionFeatureKey.Assessments], MobileIAStudentNavInternal);
//# sourceMappingURL=mobile-ia-student-nav.react-component.js.map